/**
 * Graduation checker — the subjects a student still needs to pass.
 *
 * `GraduationProgressView` renders a payload from
 * `/api/school/tenants/{tid}/students/{sid}/graduation` (registrar) or
 * `/api/school/portal/graduation` (the student's own view).
 * `GraduationCheckDialog` is the registrar wrapper around it.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  CheckCircle2, XCircle, CircleDashed, GraduationCap, AlertCircle, Award, Printer, QrCode, Loader2,
} from 'lucide-react';
import { schoolApi } from '@/components/school/schoolApi';

const STATE = {
  passed: { label: 'Passed', icon: CheckCircle2, chip: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  failed: { label: 'Retake', icon: XCircle, chip: 'bg-rose-50 text-rose-700 border-rose-200' },
  incomplete: { label: 'Part assessed', icon: AlertCircle, chip: 'bg-amber-50 text-amber-700 border-amber-200' },
  not_taken: { label: 'Not taken', icon: CircleDashed, chip: 'bg-slate-50 text-slate-600 border-slate-200' },
};

export const GraduationProgressView = ({ data, isLoading, error, mode = 'student',
                                         onIssue, issuing = false }) => {
  if (isLoading) {
    return <div className="space-y-3" data-testid="graduation-loading">
      {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
    </div>;
  }
  if (error) {
    return <p className="py-8 text-center text-sm text-rose-600" data-testid="graduation-error">
      {error.message}
    </p>;
  }
  if (!data) return null;

  const s = data.summary || {};
  const years = data.years || [];

  return (
    <div className="space-y-4" data-testid="graduation-progress">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div>
          <p className="font-semibold text-slate-900" data-testid="graduation-student-name">
            {data.student?.name}
            {data.student?.admission_number && (
              <span className="ml-2 font-mono text-xs text-slate-400">{data.student.admission_number}</span>
            )}
          </p>
          <p className="text-xs text-slate-500" data-testid="graduation-programme">
            {data.programme ? `${data.programme.name} (${data.programme.code})` : 'No course assigned'}
            {' · pass mark '}{data.pass_mark}%
          </p>
        </div>
        {s.eligible_to_graduate ? (
          <Badge className="bg-emerald-600 hover:bg-emerald-600" data-testid="graduation-eligible-badge">
            <GraduationCap className="mr-1 h-3.5 w-3.5" /> Ready to graduate
          </Badge>
        ) : !s.curriculum_ready ? (
          <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-500"
                 data-testid="graduation-no-curriculum-badge">
            No curriculum to check yet
          </Badge>
        ) : (
          <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700"
                 data-testid="graduation-outstanding-badge">
            {s.outstanding || 0} subject{s.outstanding === 1 ? '' : 's'} still needed
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[['Passed', s.passed, 'text-emerald-700'], ['Retakes', s.failed, 'text-rose-700'],
          ['Part assessed', s.incomplete, 'text-amber-700'],
          ['Not taken', s.not_taken, 'text-slate-700'],
          ['Complete', `${s.percent_complete || 0}%`, 'text-indigo-700']].map(([label, value, tone]) => (
          <Card key={label} data-testid={`graduation-stat-${label.toLowerCase().replace(/ /g, '-')}`}>
            <CardContent className="pb-3 pt-4">
              <p className={`text-xl font-bold tabular-nums ${tone}`}>{value ?? 0}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-500 transition-all"
             style={{ width: `${s.percent_complete || 0}%` }} data-testid="graduation-progress-bar" />
      </div>

      <CertificatePanel cert={data.certificate} eligible={s.eligible_to_graduate}
                        mode={mode} onIssue={onIssue} issuing={issuing} />

      {!s.curriculum_ready ? (
        <Card data-testid="graduation-no-curriculum">
          <CardContent className="py-12 text-center">
            <AlertCircle className="mx-auto mb-2 h-7 w-7 text-amber-500" />
            <p className="text-sm text-slate-600">
              {data.programme
                ? 'No subjects have been pulled into this course yet.'
                : 'This student is not on a course, so there is no curriculum to check.'}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              Build the curriculum on the Faculty page to see progress here.
            </p>
          </CardContent>
        </Card>
      ) : years.map((y) => (
        <Card key={y.id} data-testid={`graduation-year-${y.id}`}>
          <CardContent className="pt-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">
                {y.name}
                {y.is_current && (
                  <Badge variant="outline" className="ml-2 border-indigo-200 bg-indigo-50 text-[10px] text-indigo-700">
                    current year
                  </Badge>
                )}
              </div>
              <p className="text-[11px] text-slate-400" data-testid={`graduation-year-count-${y.id}`}>
                {y.passed}/{y.subjects.length} passed
              </p>
            </div>
            {y.subjects.length === 0 ? (
              <p className="py-3 text-xs text-slate-400">No subjects in this year yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {y.subjects.map((sub) => {
                  const meta = STATE[sub.status];
                  const Icon = meta.icon;
                  return (
                    <div key={sub.module_id} className="flex items-center justify-between gap-3 py-2"
                         data-testid={`graduation-subject-${sub.module_id}`}>
                      <span className="min-w-0">
                        <span className="text-sm text-slate-800">{sub.name}</span>
                        <span className="ml-2 font-mono text-[11px] text-slate-400">{sub.code}</span>
                        {sub.is_elective && (
                          <Badge variant="outline" className="ml-2 text-[10px]">elective</Badge>
                        )}
                      </span>
                      <span className="flex shrink-0 items-center gap-2">
                        {sub.percentage != null && (
                          <span className="tabular-nums text-xs text-slate-500">
                            {sub.percentage}%{sub.grade ? ` · ${sub.grade}` : ''}
                          </span>
                        )}
                        <Badge variant="outline" className={`text-[10px] ${meta.chip}`}
                               data-testid={`graduation-status-${sub.module_id}`}>
                          <Icon className="mr-1 h-3 w-3" />{meta.label}
                        </Badge>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

/** The graduation certificate: issue it, or download and verify it. */
const CertificatePanel = ({ cert, eligible, mode, onIssue, issuing }) => {
  if (!cert && !eligible) return null;

  if (!cert) {
    return (
      <Card className="border-emerald-200 bg-emerald-50" data-testid="certificate-panel">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
              <Award className="h-4 w-4" /> Every subject passed
            </p>
            <p className="text-xs text-emerald-800">
              {mode === 'admin'
                ? 'Issue the graduation certificate — it is verifiable by QR the moment you do.'
                : 'Your registrar can now issue your graduation certificate.'}
            </p>
          </div>
          {mode === 'admin' && (
            <Button onClick={onIssue} disabled={issuing} data-testid="certificate-issue-btn"
                    className="bg-emerald-700 hover:bg-emerald-800">
              {issuing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Award className="mr-1 h-4 w-4" />}
              Issue certificate
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={eligible ? 'border-emerald-200' : 'border-amber-300'} data-testid="certificate-panel">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Award className={`h-4 w-4 ${eligible ? 'text-emerald-700' : 'text-amber-600'}`} /> Graduation certificate issued
          </p>
          <p className="font-mono text-[11px] text-slate-500" data-testid="certificate-serial">
            {cert.serial} · {cert.issue_date}
          </p>
          {!eligible && (
            <p className="mt-1 text-xs text-amber-700" data-testid="certificate-not-eligible-note">
              Issued before the curriculum was met — see the Certificate audit under Lifecycle → Graduation
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" data-testid="certificate-print-btn">
            <a href={cert.print_path} target="_blank" rel="noreferrer">
              <Printer className="mr-1 h-3.5 w-3.5" /> Print / save PDF
            </a>
          </Button>
          <Button asChild size="sm" variant="outline" data-testid="certificate-verify-btn">
            <a href={cert.verify_path} target="_blank" rel="noreferrer">
              <QrCode className="mr-1 h-3.5 w-3.5" /> Verify
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const GraduationCheckDialog = ({ tenantId, student, onClose }) => {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['student-graduation', tenantId, student?.id],
    queryFn: () => schoolApi(`/tenants/${tenantId}/students/${student.id}/graduation`),
    enabled: !!student?.id,
    retry: false,
  });

  const issue = useMutation({
    mutationFn: () => schoolApi(`/tenants/${tenantId}/students/${student.id}/graduation/certificate`,
      { method: 'POST' }),
    onSuccess: (res) => {
      toast.success(res.already_issued ? 'Certificate already on file'
        : `Certificate ${res.certificate?.serial} issued`);
      qc.invalidateQueries({ queryKey: ['student-graduation', tenantId, student?.id] });
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto"
                     data-testid="graduation-check-dialog">
        <DialogHeader>
          <DialogTitle>Graduation check · {student?.first_name} {student?.last_name}</DialogTitle>
          <DialogDescription>
            Curriculum subjects measured against this student's published marks.
          </DialogDescription>
        </DialogHeader>
        <GraduationProgressView data={data} isLoading={isLoading} error={error}
                                mode="admin" issuing={issue.isPending}
                                onIssue={() => issue.mutate()} />
      </DialogContent>
    </Dialog>
  );
};

export default GraduationProgressView;
